import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  selectIsAdminUser,
  setAccess,
  switchToNonPersonal,
} from "../store/authSlice";
import { logout as logoutApi } from "../api/auth";
import { denyAccess } from "../api/access";
import { clearEmployeeRecord, getAccountIsPersonal } from "../lib/auth";

// Преизползваеми профил действия (Sidebar-ът ги ползва като самостоятелни
// редове извън dropdown-а на ProfileDropdown).

/** „Изход" с логиката на общия терминал: служител на общ акаунт → връщане към
 * пин екрана (не пълен logout); иначе — подаденият onLogout. Guard срещу
 * двоен клик. */
export function useProfileLogout(access: boolean, onLogout: () => void) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUserPersonal = useAppSelector((s) => s.auth.personal);
  const busyRef = useRef(false);
  const isSharedEmployee =
    getAccountIsPersonal() === false && currentUserPersonal === true;
  return async () => {
    if (!access && isSharedEmployee) {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        await logoutApi(); // връща session_token на споделения акаунт
      } catch (e) {
        console.error("Logout error:", e);
      }
      // Човешки жест — служителят наистина си тръгва, затова безусловно. Другите
      // прозорци виждат триенето през storage event и също падат до ПИН.
      clearEmployeeRecord();
      dispatch(switchToNonPersonal());
      navigate("/", { replace: true });
      return;
    }
    onLogout();
  };
}

/** Изход на служителя към ПИН екрана на общия терминал (както „Изход" в
 * профилното меню — споделената сесия остава, един лиценз). Връща функцията
 * само при общ акаунт с влязъл служител — иначе null. */
export function useSharedPinExit(): (() => void) | null {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUserPersonal = useAppSelector((s) => s.auth.personal);
  const busyRef = useRef(false);
  const isSharedEmployee =
    getAccountIsPersonal() === false && currentUserPersonal === true;
  const exit = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      await logoutApi(); // връща session_token на споделения акаунт
    } catch (e) {
      console.error("Logout error:", e);
    }
    // Човешки жест — безусловно (виж useProfileLogout).
    clearEmployeeRecord();
    dispatch(switchToNonPersonal());
    navigate("/", { replace: true });
    // ЗАДЪЛЖИТЕЛЕН ресет: гейтът (който вика hook-а) НЕ се демонтира при
    // смяна на route — React Router преизползва инстанцията, така че без
    // ресет всеки следващ изход към ПИН (след повторен вход с код) би бил
    // блокиран тихо от guard-а.
    busyRef.current = false;
  }, [dispatch, navigate]);
  return isSharedEmployee ? exit : null;
}

/** Вътрешни страници с междинно ниво: Esc връща една стъпка към родителската
 * страница, не направо към „Модули". */
const ESC_PARENT_PAGES: Record<string, string> = {
  "/audit-log": "/monitoring",
  "/monitoring/fiscal-issues": "/monitoring",
  "/ordering/lunch-menu": "/ordering",
};

/** Стъпката „назад" при Esc.
 * ОБЩ терминал (влязъл с код служител): от модул → страницата „Модули" (ако
 * служителят има повече от един модул), от „Модули" (или при единствен
 * модул) → първо ИЗХОД от тестовия режим (ако access=true, като
 * „Изход от тестов режим" — оставаш на място), и чак следващият Esc →
 * ПИН екранът.
 * ЛИЧЕН акаунт: от модул → „Модули" (само при >1 модула — с единствен модул
 * „Модули" веднага би те върнала обратно); на „Модули" Esc НЕ прави нищо —
 * никога изход.
 * Страница от ESC_PARENT_PAGES → първо родителската ѝ страница.
 * null когато Esc няма какво да направи (тогава listener не се закача). */
export function useEscBack(): (() => void) | null {
  const pinExit = useSharedPinExit();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Ролите в Redux са на текущо влезлия човек (при общ акаунт — служителят
  // от loginWithCode → fetchUserInfo), т.е. броенето „има ли други модули"
  // следва него, не терминала.
  const rolesCount = useAppSelector((s) => s.auth.roles.length);
  const access = useAppSelector((s) => s.auth.access);
  const revokingRef = useRef(false);
  const onModulesPage = location.pathname === "/";
  const parentPage = ESC_PARENT_PAGES[location.pathname];
  const back = useCallback(async () => {
    if (parentPage) {
      navigate(parentPage, { replace: true });
      return;
    }
    if (!onModulesPage && rolesCount > 1) {
      navigate("/", { replace: true });
      return;
    }
    // Тестовият режим е ОТДЕЛЕН слой преди изхода към ПИН: Esc на това
    // ниво само го сваля (deny-access), без да излиза. Иначе access=true
    // остава по споделената сесия (token swap не създава нова) и всеки
    // следващ служител, влязъл с код, би го наследил.
    if (access && pinExit) {
      if (revokingRef.current) return;
      revokingRef.current = true;
      try {
        await denyAccess();
      } catch (e) {
        console.error("Deny access error:", e);
      }
      dispatch(setAccess(false));
      revokingRef.current = false;
      return;
    }
    // Само общ терминал излиза към ПИН; личен акаунт тук е no-op.
    pinExit?.();
  }, [parentPage, onModulesPage, rolesCount, navigate, access, dispatch, pinExit]);
  if (pinExit || parentPage) return back;
  return rolesCount > 1 ? back : null;
}

/** Влиза в цял екран (с Keyboard Lock за Esc). Изисква потребителски жест. */
export function enterFullscreen() {
  document.documentElement
    .requestFullscreen()
    .then(() => {
      const nav = navigator as Navigator & {
        keyboard?: { lock?: (keys: string[]) => Promise<void> };
      };
      nav.keyboard?.lock?.(["Escape"]).catch(() => {});
    })
    .catch(() => {});
}

/** Цял екран: състояние + toggle (с Keyboard Lock за Esc). */
export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(
    !!document.fullscreenElement,
  );
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggle = () => {
    if (!document.fullscreenElement) {
      enterFullscreen();
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };
  return { isFullscreen, toggle };
}

/** Първият клик/тап в модула вкарва браузъра в цял екран. Админският акаунт
 * (user id 1) е изключен от поведението; преди зареждане на userId не се
 * прави нищо. Слушателят е на "click" (не pointerdown), защото при touch
 * екран pointerdown още не носи user activation и requestFullscreen би бил
 * отхвърлен; capture гарантира, че stopPropagation в компонент няма да го
 * глътне. One-shot е и не пречи на pointerdown отключването на звука в
 * мониторите. */
export function useFirstClickFullscreen() {
  const isAdminUser = useAppSelector(selectIsAdminUser);
  const userId = useAppSelector((s) => s.auth.userId);
  useEffect(() => {
    if (userId == null || isAdminUser) return;
    const onFirstGesture = () => {
      if (!document.fullscreenElement) enterFullscreen();
    };
    window.addEventListener("click", onFirstGesture, {
      once: true,
      capture: true,
    });
    return () =>
      window.removeEventListener("click", onFirstGesture, { capture: true });
  }, [userId, isAdminUser]);
}
